import { ActionCreator } from 'redux';
import { ContactsList, ContactStatus } from '@dev/ee-api/ee-api-models';

import { ListFromCampaignParams } from '@/services/ElasticApi';
import { ApplicationState } from '@/store';

import {
    ListAddContactsErrorAction,
    ListAddContactsStartAction,
    ListAddContactsSuccessAction,
    ListAddErrorAction,
    ListAddStartAction,
    ListAddSuccessAction,
    ListCreateNthErrorAction,
    ListCreateNthStartAction,
    ListCreateNthSuccessAction,
    ListCreateRandomErrorAction,
    ListCreateRandomStartAction,
    ListCreateRandomSuccessAction,
    ListDeleteErrorAction,
    ListDeleteStartAction,
    ListDeleteSuccessAction,
    ListDeselectAction,
    ListEditErrorAction,
    ListEditStartAction,
    ListEditSuccessAction,
    ListFromCampaignErrorAction,
    ListFromCampaignStartAction,
    ListFromCampaignSuccessAction,
    ListLoadByContactErrorAction,
    ListLoadByContactStartAction,
    ListLoadByContactSuccessAction,
    ListLoadFetchErrorAction,
    ListLoadFetchStartAction,
    ListLoadFetchSuccessAction,
    ListLoadSingleFetchErrorAction,
    ListLoadSingleFetchStartAction,
    ListLoadSingleFetchSuccessAction,
    ListMoveContactsErrorAction,
    ListMoveContactsStartAction,
    ListMoveContactsSuccessAction,
    ListPrepareParamsAction,
    ListRemoveContactsErrorAction,
    ListRemoveContactsStartAction,
    ListRemoveContactsSuccessAction,
    ListSelectAction,
    ListSetParamsAction,
    ListsGetNamesErrorAction,
    ListsGetNamesStartAction,
    ListsGetNamesSuccessAction,
    ListsLoadHistoryTrackedError,
    ListsLoadHistoryTrackedStart,
    ListsLoadHistoryTrackedSuccess,
    ListsState,
    NewList,
    SetCollectionAction,
} from './types';

export const SingleListLoadStart: ActionCreator<ListLoadSingleFetchStartAction> = (
    listname: string
): ListLoadSingleFetchStartAction => ({
    type: '@@lists/SINGLE_FETCH_START',
    payload: {
        isListLoading: true,
        listname,
    },
});

export const ListLoadSingleError: ActionCreator<
    ListLoadSingleFetchErrorAction
> = (): ListLoadSingleFetchErrorAction => ({
    type: '@@lists/SINGLE_FETCH_ERROR',
    payload: {
        isListLoading: false,
    },
});

export const ListLoadSingleSuccess: ActionCreator<ListLoadSingleFetchSuccessAction> = (
    selected: ContactsList
): ListLoadSingleFetchSuccessAction => ({
    type: '@@lists/SINGLE_FETCH_SUCCESS',
    payload: {
        selected,
        isListLoading: false,
    },
});

export const ListsLoadStart = (params?: {
    search?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    includeHistory?: boolean;
    modifyQueryString?: boolean;
    getAllElements?: boolean;
}): ListLoadFetchStartAction => ({
    type: '@@lists/FETCH_START',
    payload: {
        search: params?.search,
        from: params?.from,
        to: params?.to,
        limit: params?.limit,
        offset: params?.offset,
        includeHistory: params?.includeHistory || false,
        isListLoading: true,
        modifyQueryString: params?.modifyQueryString !== undefined ? params.modifyQueryString : true,
        getAllElements: params?.getAllElements,
    },
});

export const ListsLoadError: ActionCreator<ListLoadFetchErrorAction> = (): ListLoadFetchErrorAction => ({
    type: '@@lists/FETCH_ERROR',
    payload: {
        isListLoading: false,
    },
});

export const ListsLoadSuccess: ActionCreator<ListLoadFetchSuccessAction> = (
    collection: ContactsList[],
    totalCount: number,
    totalTrackedLists: number
): ListLoadFetchSuccessAction => ({
    type: '@@lists/FETCH_SUCCESS',
    payload: {
        collection,
        totalCount,
        totalTrackedLists,
        isListLoading: false,
    },
});

export const ListsGetNamesStart: ActionCreator<ListsGetNamesStartAction> = (): ListsGetNamesStartAction => ({
    type: '@@lists/FETCH_LISTNAMES_START',
});

export const ListsGetNamesError: ActionCreator<ListsGetNamesErrorAction> = (): ListsGetNamesErrorAction => ({
    type: '@@lists/FETCH_LISTNAMES_ERROR',
});

export const ListsGetNamesSuccess: ActionCreator<ListsGetNamesSuccessAction> = (
    listNamesCollection: ContactsList[]
): ListsGetNamesSuccessAction => ({
    type: '@@lists/FETCH_LISTNAMES_SUCCESS',
    payload: {
        listNamesCollection,
    },
});

export const ListDeleteStart = (
    name: string,
    perPage?: number,
    activePage?: number,
    search?: string
): ListDeleteStartAction => ({
    type: '@@lists/DELETE_START',
    payload: {
        name,
        perPage,
        activePage,
        search,
    },
    meta: {
        thunk: true,
    },
});

export const ListDeleteError = (meta: Record<string, unknown>): ListDeleteErrorAction => ({
    type: '@@lists/DELETE_ERROR',
    meta,
    error: true,
});

export const ListDeleteSuccess: ActionCreator<ListDeleteSuccessAction> = (
    collection: ContactsList[]
): ListDeleteSuccessAction => ({
    type: '@@lists/DELETE_SUCCESS',
    payload: {
        collection,
    },
});

export const ListAddStart = (list: NewList): ListAddStartAction => ({
    type: '@@lists/ADD_START',
    payload: {
        list,
    },
    meta: {
        thunk: true,
    },
});

export const ListAddError: ActionCreator<ListAddErrorAction> = (meta: Record<string, any>): ListAddErrorAction => ({
    type: '@@lists/ADD_ERROR',
    error: true,
    meta,
});

export const ListAddSuccess: ActionCreator<ListAddSuccessAction> = (meta): ListAddSuccessAction => ({
    type: '@@lists/ADD_SUCCESS',
    meta,
});

export const ListEditStart = (
    listname: string,
    body: { NewListName: string; AllowUnsubscribe: boolean; TrackHistory?: boolean }
): ListEditStartAction => ({
    type: '@@lists/EDIT_START',
    payload: {
        listname,
        body,
    },
});

export const ListEditError: ActionCreator<ListEditErrorAction> = (): ListEditErrorAction => ({
    type: '@@lists/EDIT_ERROR',
});

export const ListEditSuccess = (collection: ContactsList[]): ListEditSuccessAction => ({
    type: '@@lists/EDIT_SUCCESS',
    payload: {
        collection,
    },
});

export const ListCreateRandomStart: ActionCreator<ListCreateRandomStartAction> = (
    list: NewList
): ListCreateRandomStartAction => ({
    type: '@@lists/CREATE_RANDOM_START',
    payload: {
        list,
    },
});

export const ListCreateRandomError: ActionCreator<ListCreateRandomErrorAction> = (): ListCreateRandomErrorAction => ({
    type: '@@lists/CREATE_RANDOM_ERROR',
    payload: {
        isListLoading: false,
    },
});

export const ListCreateRandomSuccess: ActionCreator<ListCreateRandomSuccessAction> = (
    list: ContactsList
): ListCreateRandomSuccessAction => ({
    type: '@@lists/CREATE_RANDOM_SUCCESS',
    payload: {
        list,
    },
});

export const ListCreateNthStart: ActionCreator<ListCreateNthStartAction> = (
    list: NewList
): ListCreateNthStartAction => ({
    type: '@@lists/CREATE_NTH_START',
    payload: {
        list,
        isNthListCreated: true,
    },
});

export const ListCreateNthError: ActionCreator<ListCreateNthErrorAction> = (): ListCreateNthErrorAction => ({
    type: '@@lists/CREATE_NTH_ERROR',
    payload: {
        isNthListCreated: false,
    },
});

export const ListCreateNthSuccess: ActionCreator<ListCreateNthSuccessAction> = (): ListCreateNthSuccessAction => ({
    type: '@@lists/CREATE_NTH_SUCCESS',
    payload: {
        isNthListCreated: false,
    },
});

export const ListAddContactsStart = (
    listName: string,
    body: { emails?: string[]; rule?: string; allContacts?: boolean }
): ListAddContactsStartAction => ({
    type: '@@lists/ADD_CONTACTS_START',
    payload: {
        listName,
        body,
    },
});

export const ListAddContactsError: ActionCreator<ListAddContactsErrorAction> = (): ListAddContactsErrorAction => ({
    type: '@@lists/ADD_CONTACTS_ERROR',
});

export const ListAddContactsSuccess: ActionCreator<
    ListAddContactsSuccessAction
> = (): ListAddContactsSuccessAction => ({
    type: '@@lists/ADD_CONTACTS_SUCCESS',
});

export const ListMoveContactsStart: ActionCreator<ListMoveContactsStartAction> = (
    listName: string,
    body: { newListName: string; statuses?: ContactStatus[]; emails?: string[]; rule?: string; allContacts?: boolean }
): ListMoveContactsStartAction => ({
    type: '@@lists/MOVE_CONTACTS_START',
    payload: {
        listName,
        body,
    },
});

export const ListMoveContactsError: ActionCreator<ListMoveContactsErrorAction> = (): ListMoveContactsErrorAction => ({
    type: '@@lists/MOVE_CONTACTS_ERROR',
});

export const ListMoveContactsSuccess: ActionCreator<
    ListMoveContactsSuccessAction
> = (): ListMoveContactsSuccessAction => ({
    type: '@@lists/MOVE_CONTACTS_SUCCESS',
});

export const ListRemoveContactsStart: ActionCreator<ListRemoveContactsStartAction> = (
    listName: string,
    body: { Emails?: string[]; Rule?: string },
    limit?: number,
    offset?: number,
    statuses?: string[]
): ListRemoveContactsStartAction => ({
    type: '@@lists/REMOVE_CONTACTS_START',
    payload: {
        listName,
        body,
        limit,
        offset,
        statuses,
    },
});

export const ListRemoveContactsError: ActionCreator<
    ListRemoveContactsErrorAction
> = (): ListRemoveContactsErrorAction => ({
    type: '@@lists/REMOVE_CONTACTS_ERROR',
});

export const ListRemoveContactsSuccess: ActionCreator<
    ListRemoveContactsSuccessAction
> = (): ListRemoveContactsSuccessAction => ({
    type: '@@lists/REMOVE_CONTACTS_SUCCESS',
});

export const ListPrepareParams: ActionCreator<ListPrepareParamsAction> = (
    newList: NewList
): ListPrepareParamsAction => ({
    type: '@@lists/CREATE_NEW_PARAMS',
    payload: {
        newList,
    },
});

export const ListSetParams: ActionCreator<ListSetParamsAction> = (newList: NewList): ListSetParamsAction => ({
    type: '@@lists/SET_NEW_PARAMS',
    payload: {
        newList,
    },
});

export const ListSelect: ActionCreator<ListSelectAction> = (selected: ContactsList): ListSelectAction => ({
    type: '@@lists/SELECT',
    payload: {
        selected,
    },
});

export const ListDeselect: ActionCreator<ListDeselectAction> = (): ListDeselectAction => ({
    type: '@@lists/DESELECT',
    payload: {
        selected: null,
    },
});

export const SetListCollection: ActionCreator<SetCollectionAction> = (
    visibleSet: ContactsList[]
): SetCollectionAction => ({
    type: '@@lists/SET_COLLECTION',
    payload: {
        visibleSet,
    },
});

export const ListsLoadHistoryTrackedStartAction: ActionCreator<ListsLoadHistoryTrackedStart> = (
    includeHistory?: boolean,
    from?: string,
    to?: string
): ListsLoadHistoryTrackedStart => ({
    type: '@@lists/LOAD_HISTORY_TRACKED_START',
    payload: {
        isListLoading: true,
        includeHistory,
        from,
        to,
    },
});

export const ListsLoadHistoryTrackedErrorAction: ActionCreator<
    ListsLoadHistoryTrackedError
> = (): ListsLoadHistoryTrackedError => ({
    type: '@@lists/LOAD_HISTORY_TRACKED_ERROR',
    payload: {
        isListLoading: false,
    },
});

export const ListsLoadHistoryTrackedSuccessAction: ActionCreator<ListsLoadHistoryTrackedSuccess> = (
    historyTrackedCollection: ContactsList[]
): ListsLoadHistoryTrackedSuccess => ({
    type: '@@lists/LOAD_HISTORY_TRACKED_SUCCESS',
    payload: {
        isListLoading: false,
        historyTrackedCollection,
    },
});

export const ListLoadByContactStart: ActionCreator<ListLoadByContactStartAction> = (
    email: string,
    showErrorToast = false
): ListLoadByContactStartAction => ({
    type: '@@lists/LOAD_BY_CONTACT_START',
    payload: {
        email,
        showErrorToast,
    },
});

export const ListLoadByContactError: ActionCreator<
    ListLoadByContactErrorAction
> = (): ListLoadByContactErrorAction => ({
    type: '@@lists/LOAD_BY_CONTACT_ERROR',
});

export const ListLoadByContactSuccess: ActionCreator<ListLoadByContactSuccessAction> = (
    collection: ContactsList[]
): ListLoadByContactSuccessAction => ({
    type: '@@lists/LOAD_BY_CONTACT_SUCCESS',
    payload: {
        collection,
    },
});

export const ListFromCampaignStart: ActionCreator<ListFromCampaignStartAction> = (
    params: ListFromCampaignParams
): ListFromCampaignStartAction => ({
    type: '@@lists/LIST_FROM_CAMPAIGN_START',
    payload: {
        params,
    },
});

export const ListFromCampaignError: ActionCreator<ListFromCampaignErrorAction> = (): ListFromCampaignErrorAction => ({
    type: '@@lists/LIST_FROM_CAMPAIGN_ERROR',
});

export const ListFromCampaignSuccess: ActionCreator<
    ListFromCampaignSuccessAction
> = (): ListFromCampaignSuccessAction => ({
    type: '@@lists/LIST_FROM_CAMPAIGN_SUCCESS',
});

export const getListstStore = (store: ApplicationState): ListsState => store.lists;
export const getListsCount = (state: ApplicationState): number => state.lists.totalCount;
export const getAllContactsLists = (store: ApplicationState) => store.lists.collection;
export const getContactListsNames = (store: ApplicationState) =>
    store.lists.listNamesCollection || store.lists.collection;
export const getSelectedList = (store: ApplicationState) => store?.lists?.selected;
